import { z } from 'zod';
import type { CountryCode } from './country.js';

/**
 * F43 — GDPR request-tracking scaffolding (D5, §2.8).
 *
 * MVP ships intake + SLA-tracked admin queues + audit + F26 confirmation only.
 * The "process delete" action reuses today's F07 soft-delete + revokeAllSessions
 * — data STAYS, no PII scrub. The real export-archive builder + erase cascade
 * are explicitly POST-MVP (§11). `sla_due_at = requested_at + 30 days`; one open
 * request per (user × kind) is enforced by the DB partial-unique index
 * (→ GDPR_REQUEST_ALREADY_OPEN).
 *
 * Cross-bundler note: imports use `.js` extensions per the F03 cross-bundler seam.
 *
 * See plans/43_F43_admin_user_management.md §2.8 / §4.
 */

// ─── Constants ──────────────────────────────────────────────────────────────

/** The SLA window the admin UI counts down; the service sets requested_at + 30d. */
export const GDPR_SLA_DAYS = 30;
export const GDPR_RESOLUTION_NOTE_MAX = 1000;

// ─── Enums ──────────────────────────────────────────────────────────────────

export const GDPR_REQUEST_KINDS = ['export', 'delete'] as const;
export type GdprRequestKind = (typeof GDPR_REQUEST_KINDS)[number];
export const GdprRequestKindSchema = z.enum(GDPR_REQUEST_KINDS);

export const GDPR_REQUEST_STATUSES = [
  'open',
  'in_review',
  'fulfilled',
  'rejected',
  'cancelled',
] as const;
export type GdprRequestStatus = (typeof GDPR_REQUEST_STATUSES)[number];
export const GdprRequestStatusSchema = z.enum(GDPR_REQUEST_STATUSES);

export const GDPR_CREATED_BY_KINDS = ['user', 'admin'] as const;
export type GdprCreatedByKind = (typeof GDPR_CREATED_BY_KINDS)[number];
export const GdprCreatedByKindSchema = z.enum(GDPR_CREATED_BY_KINDS);

// ─── Input schemas ────────────────────────────────────────────────────────────

/** POST /me/gdpr-requests — a consumer files an export/delete request. */
export const CreateGdprRequestInputSchema = z
  .object({
    kind: GdprRequestKindSchema,
  })
  .strict();
export type CreateGdprRequestInput = z.infer<typeof CreateGdprRequestInputSchema>;

/**
 * POST /admin/gdpr-requests/:id/process — fulfil a request. For delete →
 * softDelete + revokeAllSessions (data stays); for export → mark fulfilled with
 * a `resolutionNote` (manual, no archive builder). For /reject → status='rejected'.
 */
export const ProcessGdprRequestInputSchema = z
  .object({
    resolutionNote: z.string().trim().min(1).max(GDPR_RESOLUTION_NOTE_MAX).optional(),
  })
  .strict();
export type ProcessGdprRequestInput = z.infer<typeof ProcessGdprRequestInputSchema>;

// ─── Read shape ────────────────────────────────────────────────────────────────

/** One row in `gdpr_requests` (admin queue + consumer intake confirmation). */
export interface GdprRequest {
  readonly id: string;
  readonly userId: string;
  readonly kind: GdprRequestKind;
  readonly status: GdprRequestStatus;
  readonly requestedAt: string;
  /** requested_at + 30 days; the admin UI renders a countdown banner. */
  readonly slaDueAt: string;
  readonly createdByKind: GdprCreatedByKind;
  readonly reviewedByAdminId: string | null;
  readonly reviewedAt: string | null;
  readonly fulfilledAt: string | null;
  readonly resolutionNote: string | null;
  readonly countryCode: CountryCode;
  readonly createdAt: string;
  readonly updatedAt: string;
}
